// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon, IconButton, TagChipGroup, TagPicker } from '@loa/ui-components';
import { useTags } from '../../contexts/TagsContext';
import { useEntityTagEditor } from '../../hooks/useEntityTagEditor';
import type { TagDTO, TaggableEntityType } from '../../types/tag';
import './EntityTagEditor.css';

export interface EntityTagEditorProps {
  entityType: TaggableEntityType;
  entityId: string;
  /** The entity's currently-assigned tags (from a detail fetch or batch decoration). */
  tags: TagDTO[];
  /** Called with the fresh tag list after a successful edit, so the caller can update its own state. */
  onTagsChange?: (tags: TagDTO[]) => void;
  /** Max chips shown before the "+k" overflow (TagChipGroup default is 3). */
  maxChips?: number;
  className?: string;
}

/**
 * Detail-page / row affordance: a read-only {@link TagChipGroup} plus a
 * pencil button that opens a {@link TagPicker} popover for editing. Renders
 * nothing when the tags feature is off for the tenant (probe via
 * `useTags().featureEnabled`).
 */
export function EntityTagEditor({
  entityType,
  entityId,
  tags: initialTags,
  onTagsChange,
  maxChips = 3,
  className = '',
}: Readonly<EntityTagEditorProps>) {
  const { tags: allTags, loading: tagsLoading, featureEnabled } = useTags();
  const editor = useEntityTagEditor(entityType, entityId, initialTags);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  // IconButton is not forwardRef — wrap it in a plain span to anchor the popover.
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({ top: rect.bottom + 4, left: rect.left });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!featureEnabled) return null;

  const selectedIds = editor.tags.map((t) => t.id);
  const pickerTags = allTags.filter((t) => !t.archived || selectedIds.includes(t.id));

  const handleToggle = async (tagId: string) => {
    const next = selectedIds.includes(tagId)
      ? selectedIds.filter((id) => id !== tagId)
      : [...selectedIds, tagId];
    try {
      await editor.replace(next, (id) => allTags.find((t) => t.id === id));
      onTagsChange?.(editor.tags);
    } catch {
      // useEntityTagEditor already rolled back optimistic state + set editor.error
    }
  };

  const handleCreate = async (name: string) => {
    try {
      await editor.createAndAssign(name);
      onTagsChange?.(editor.tags);
    } catch {
      // error surfaced via editor.error
    }
  };

  const popover = open && position ? createPortal(
    <div ref={popoverRef} className="entity-tag-editor__popover" style={{ top: position.top, left: position.left }}>
      <TagPicker
        tags={pickerTags.map((t) => ({ id: t.id, name: t.name, color: t.color, archived: t.archived ?? false }))}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onCreate={handleCreate}
        loading={tagsLoading}
        error={editor.error}
        creating={editor.saving}
      />
    </div>,
    document.body,
  ) : null;

  return (
    <div className={`entity-tag-editor ${className}`.trim()}>
      <TagChipGroup
        tags={editor.tags.map((t) => ({ id: t.id, name: t.name, color: t.color, archived: t.archived ?? false }))}
        max={maxChips}
      />
      <span ref={triggerRef} className="entity-tag-editor__trigger-anchor">
        <IconButton
          variant="ghost"
          size="xs"
          aria-label="Edit tags"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          icon={<AppIcon name="edit" size={14} />}
        />
      </span>
      {popover}
    </div>
  );
}
